
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useRef, useEffect, useMemo } from "react";
import { GoogleGenAI, Chat } from "@google/genai";
import { Send, Bot, User, MoreVertical, Settings, BookOpen, Trash2, Info, ShieldCheck, Zap } from "lucide-react";
import { ChatMessage } from "../../types";
import { Suggestions } from "./chat/Suggestions";
import { Card } from "../ui/Card";
import { ChatSettingsModal } from "./chat/ChatSettingsModal";
import { PromptLibraryModal } from "./chat/PromptLibraryModal";
import { BarChart } from "../charts/BarChart";
import { AnimatePresence, motion } from "framer-motion";
import { getPrompts } from "../../services/promptService";

const ChatFeeCard = ({ amount, networkFee, serviceFee, total }: any) => (
    <div className="p-4 bg-stone-50 dark:bg-black/40 rounded-xl border border-stone-200 dark:border-white/10 space-y-3 mt-2 shadow-inner w-full max-w-sm">
        <div className="flex justify-between items-center text-[10px] font-bold text-stone-500 uppercase tracking-widest">
            <span>Base Amount</span>
            <span className="text-stone-900 dark:text-white font-mono">${amount.toFixed(2)}</span>
        </div>
        <div className="flex justify-between items-center text-[10px] font-bold text-stone-500 uppercase tracking-widest">
            <span>Service Fee</span>
            <span className="text-stone-900 dark:text-white font-mono">${serviceFee.toFixed(2)}</span>
        </div>
        <div className="flex justify-between items-center text-[10px] font-bold text-stone-500 uppercase tracking-widest">
            <span>Gas / Network</span>
            <span className="text-stone-900 dark:text-white font-mono">${networkFee.toFixed(2)}</span>
        </div>
        <div className="pt-2 border-t border-stone-200 dark:border-white/10 flex justify-between items-center">
            <span className="text-xs font-bold text-[#0A0B0F] dark:text-white uppercase tracking-widest">Estimated Total</span>
            <span className="text-lg font-mono font-bold text-nobel-gold">${total.toFixed(2)}</span>
        </div>
        <div className="pt-1 flex items-center gap-2 text-[8px] text-emerald-500 font-black uppercase tracking-widest">
            <ShieldCheck size={10}/> GhostOS Isolated Routing Active
        </div>
    </div>
);

export const ChatView: React.FC = () => {
  const initialMessage: ChatMessage = { id: '1', sender: 'agent', text: 'Hello! I am your GhostWallet Assistant. How can I help you with your transactions or POS setup today?', timestamp: new Date() };
  
  const [messages, setMessages] = useState<ChatMessage[]>([initialMessage]);
  const [input, setInput] = useState("");
  const [isBotThinking, setIsBotThinking] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showPromptLibrary, setShowPromptLibrary] = useState(false);
  const [isMenuOpen, setMenuOpen] = useState(false);
  
  const [aiSettings, setAiSettings] = useState(() => {
    try {
        const saved = localStorage.getItem('ghost-ai-settings');
        if (saved) {
            const parsed = JSON.parse(saved);
            return {
                model: parsed.model || 'gemini-3-flash-preview',
                personality: parsed.personality || 'You are a helpful assistant for the GhostPay ecosystem. You can analyze transactions, recommend products, and initiate escrow contracts. When asked to create a chart or visualize data, respond ONLY with a JSON object with this exact structure: `{"type": "chart", "chartType": "bar", "data": {"labels": [], "datasets": [{"label": "", "data": []}]}, "text": "Optional summary text."}`. When asked for fee estimates, respond with a JSON object: `{"type": "fee", "amount": 100, "networkFee": 0.05, "serviceFee": 2.5, "total": 102.55, "text": "Summary."}`. For all other queries, respond with clear, user-friendly text.',
                enableHistory: parsed.enableHistory !== false,
            };
        }
    } catch (e) { console.error("Failed to parse AI settings from localStorage", e); }
    return {
        model: 'gemini-3-flash-preview',
        personality: 'You are a helpful assistant for the GhostPay ecosystem. You can analyze transactions, recommend products, and initiate escrow contracts. When asked to create a chart or visualize data, respond ONLY with a JSON object with this exact structure: `{"type": "chart", "chartType": "bar", "data": {"labels": [], "datasets": [{"label": "", "data": []}]}, "text": "Optional summary text."}`. When asked for fee estimates, respond with a JSON object: `{"type": "fee", "amount": 100, "networkFee": 0.05, "serviceFee": 2.5, "total": 102.55, "text": "Summary."}`. For all other queries, respond with clear, user-friendly text.',
        enableHistory: true,
    };
  });
  
  const messagesEndRef = useRef<null | HTMLDivElement>(null);
  const chatRef = useRef<Chat | null>(null);
  const aiRef = useRef<GoogleGenAI | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    localStorage.setItem('ghost-ai-settings', JSON.stringify(aiSettings));
    aiRef.current = null;
    chatRef.current = null;
  }, [aiSettings]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
        if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
            setMenuOpen(false);
        }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(scrollToBottom, [messages, isBotThinking]);

  const handleSend = async () => {
    if (!input.trim() || input.length > 2000) return;
    
    const newUserMsg: ChatMessage = { id: Date.now().toString(), sender: 'user', text: input, timestamp: new Date() };
    setMessages(prev => [...prev, newUserMsg]);
    const currentInput = input;
    setInput("");
    setIsBotThinking(true);

    try {
        const key = process.env.API_KEY;
        if (!key) throw new Error("API key is not configured.");

        if (!aiRef.current) {
            aiRef.current = new GoogleGenAI({ apiKey: key });
        }
        
        let botText = "";
        
        if (aiSettings.enableHistory) {
            if (!chatRef.current) {
                 chatRef.current = aiRef.current.chats.create({
                    model: aiSettings.model,
                    config: { systemInstruction: aiSettings.personality },
                    history: messages.filter(m => !m.isError).map(m => {
                        let content = m.text;
                        try {
                           const parsed = JSON.parse(m.text);
                           if (['chart', 'fee'].includes(parsed.type) && parsed.text) {
                               content = parsed.text;
                           }
                        } catch (e) { /* Not a JSON object */ }
                        return {
                            role: m.sender === 'user' ? 'user' : 'model',
                            parts: [{ text: content }]
                        }
                    })
                });
            }
            const response = await chatRef.current.sendMessage({ message: currentInput });
            botText = response.text ?? "Sorry, I couldn't generate a response.";
        } else {
            chatRef.current = null;
            const response = await aiRef.current.models.generateContent({
                model: aiSettings.model,
                contents: currentInput,
                config: { systemInstruction: aiSettings.personality }
            });
            botText = response.text ?? "Sorry, I couldn't generate a response.";
        }

        const botResponse: ChatMessage = { id: (Date.now() + 1).toString(), sender: 'agent', text: botText, timestamp: new Date() };
        setMessages(prev => [...prev, botResponse]);

    } catch (error: any) {
        console.error("Gemini API error:", error);
        let errorMessage = "Sorry, I encountered an error. Please try again.";
        const errorResponse: ChatMessage = { id: (Date.now() + 1).toString(), sender: 'agent', text: errorMessage, timestamp: new Date(), isError: true };
        setMessages(prev => [...prev, errorResponse]);
    } finally {
        setIsBotThinking(false);
    }
  };
  
  const handleSaveSettings = (newSettings: { model: string; personality: string; enableHistory: boolean; }) => {
      setAiSettings(newSettings);
      setShowSettingsModal(false);
  };
  
  const handleClearChat = () => {
    setMessages([initialMessage]);
    chatRef.current = null;
    setMenuOpen(false);
  };

  const suggestionPrompts = useMemo(() => {
    return getPrompts()
        .filter(p => !p.isCustom)
        .slice(0, 4)
        .map(p => p.text);
  }, []);

  return (
    <>
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 h-full">
       <div className="lg:col-span-2 h-full flex flex-col">
            <Card noPadding className="flex-1 flex flex-col overflow-hidden">
              <div className="p-4 border-b border-stone-200 dark:border-white/5 flex justify-between items-center bg-stone-50/50 dark:bg-white/[0.02]">
                 <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-stone-900 dark:bg-white rounded-full flex items-center justify-center text-nobel-gold dark:text-[#0A0B0F]">
                       <Bot size={20} />
                    </div>
                    <div>
                       <div className="font-serif font-bold text-stone-900 dark:text-white">Ghost Assistant</div>
                       <div className="text-xs text-green-600 dark:text-green-400 flex items-center gap-1"><span className="w-1.5 h-1.5 bg-green-500 rounded-full"></span> Online</div>
                    </div>
                 </div>
                 <div className="flex items-center gap-2">
                    <button onClick={() => setShowPromptLibrary(true)} className="p-2 text-stone-400 hover:text-stone-600 dark:hover:text-white rounded-lg hover:bg-stone-100 dark:hover:bg-white/10 transition-colors"><BookOpen size={20} /></button>
                    <button onClick={() => setShowSettingsModal(true)} className="p-2 text-stone-400 hover:text-stone-600 dark:hover:text-white rounded-lg hover:bg-stone-100 dark:hover:bg-white/10 transition-colors"><Settings size={20} /></button>
                    <div className="relative" ref={menuRef}>
                        <button onClick={() => setMenuOpen(!isMenuOpen)} className="p-2 text-stone-400 hover:text-stone-600 dark:hover:text-white rounded-lg hover:bg-stone-100 dark:hover:bg-white/10 transition-colors">
                            <MoreVertical size={20} />
                        </button>
                        <AnimatePresence>
                        {isMenuOpen && (
                            <motion.div
                                initial={{ opacity: 0, y: -10, scale: 0.95 }}
                                animate={{ opacity: 1, y: 0, scale: 1 }}
                                exit={{ opacity: 0, y: -10, scale: 0.95 }}
                                className="absolute top-full right-0 mt-2 w-48 bg-white dark:bg-[#1A1D23] border border-stone-200 dark:border-[#2D3142] rounded-lg shadow-xl z-10"
                            >
                                <button
                                    onClick={handleClearChat}
                                    className="w-full flex items-center gap-3 text-left px-4 py-2 text-sm text-stone-700 dark:text-stone-300 hover:bg-stone-100 dark:hover:bg-white/10 transition-colors"
                                >
                                    <Trash2 size={14} /> Clear Chat
                                </button>
                            </motion.div>
                        )}
                        </AnimatePresence>
                    </div>
                 </div>
              </div>

              <div className="flex-1 overflow-y-auto p-6 space-y-4 bg-[#F9F8F4] dark:bg-[#0A0B0F]/50 custom-scrollbar">
                 {messages.map((msg) => {
                     let isChart = false;
                     let isFee = false;
                     let chartData = null;
                     let feeData = null;
                     let outputText = '';
                     if (msg.sender === 'agent' && !msg.isError) {
                         try {
                             const parsed = JSON.parse(msg.text);
                             if (parsed.type === 'chart' && parsed.data) {
                                 isChart = true;
                                 chartData = parsed.data;
                                 outputText = parsed.text;
                             } else if (parsed.type === 'fee') {
                                 isFee = true;
                                 feeData = parsed;
                                 outputText = parsed.text;
                             }
                         } catch (e) { /* Not a JSON object */ }
                     }
                    return (
                    <div key={msg.id} className="animate-fade-in">
                      <div className={`flex items-start ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
                         <div className={`flex gap-3 max-w-[80%] ${msg.sender === 'user' ? 'flex-row-reverse' : 'flex-row'}`}>
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${msg.sender === 'user' ? 'bg-stone-200 text-stone-600 dark:bg-white/10 dark:text-white' : 'bg-stone-900 text-nobel-gold dark:bg-white dark:text-[#0A0B0F]'}`}>
                               {msg.sender === 'user' ? <User size={14} /> : <Bot size={14} />}
                            </div>
                            <div className={`p-4 rounded-2xl text-sm leading-relaxed shadow-sm ${msg.isError ? 'bg-red-100 dark:bg-red-500/10 text-red-800 dark:text-red-300 rounded-tl-none border border-red-200 dark:border-red-500/20' : msg.sender === 'user' ? 'bg-white dark:bg-[#1A1D23] text-stone-800 dark:text-gray-200 rounded-tr-none border border-stone-100 dark:border-white/5' : 'bg-stone-800 dark:bg-white text-stone-100 dark:text-[#0A0B0F] rounded-tl-none'}`}>
                               {isChart && chartData ? (
                                    <div className="space-y-2">
                                        {outputText && <p>{outputText}</p>}
                                        <BarChart data={chartData} />
                                    </div>
                                ) : isFee && feeData ? (
                                    <div className="space-y-2">
                                        {outputText && <p>{outputText}</p>}
                                        <ChatFeeCard {...feeData} />
                                    </div>
                                ) : (
                                    msg.text
                                )}
                            </div>
                         </div>
                      </div>
                      <div className={`text-[10px] text-stone-400 mt-1.5 ${msg.sender === 'user' ? 'text-right pr-11' : 'text-left pl-11'}`}>
                        {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </div>
                    </div>
                 )})}

                 {isBotThinking && (
                    <div className="flex justify-start animate-fade-in">
                        <div className="flex gap-3 max-w-[80%] flex-row">
                            <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 bg-stone-900 text-nobel-gold dark:bg-white dark:text-[#0A0B0F]">
                                <Bot size={14} />
                            </div>
                            <div className="p-4 rounded-2xl text-sm leading-relaxed shadow-sm bg-stone-800 dark:bg-white text-stone-100 dark:text-[#0A0B0F] rounded-tl-none flex items-center gap-2">
                                <span className="w-2 h-2 bg-stone-400 dark:bg-stone-500 rounded-full animate-pulse" style={{ animationDelay: '0s' }}></span>
                                <span className="w-2 h-2 bg-stone-400 dark:bg-stone-500 rounded-full animate-pulse" style={{ animationDelay: '0.2s' }}></span>
                                <span className="w-2 h-2 bg-stone-400 dark:bg-stone-500 rounded-full animate-pulse" style={{ animationDelay: '0.4s' }}></span>
                            </div>
                        </div>
                    </div>
                 )}

                 <div ref={messagesEndRef} />
              </div>

              <div className="p-4 bg-white dark:bg-[#15171C] border-t border-stone-200 dark:border-white/5">
                 <div className="flex items-center gap-2 mb-3 overflow-x-auto no-scrollbar pb-1">
                    <span className="text-xs font-bold text-stone-400 whitespace-nowrap">Try:</span>
                    {suggestionPrompts.map((prompt) => (
                        <button 
                            key={prompt}
                            onClick={() => setInput(prompt)}
                            className="px-3 py-1.5 text-xs font-medium text-stone-600 dark:text-stone-300 bg-stone-100 dark:bg-white/5 rounded-full hover:bg-stone-200 dark:hover:bg-white/10 whitespace-nowrap transition-colors"
                        >
                            {prompt}
                        </button>
                    ))}
                </div>
                 <div className="relative">
                    <input 
                        type="text" 
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleSend()}
                        placeholder="Ask about transactions, analytics, or support..."
                        className="w-full bg-stone-50 dark:bg-black/20 border border-stone-200 dark:border-white/10 rounded-btn px-4 py-3 focus:outline-none focus:border-nobel-gold dark:focus:border-white transition-colors text-sm text-[#0A0B0F] dark:text-white pr-28"
                    />
                     <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-3">
                        <span className={`text-xs font-mono ${input.length > 2000 ? 'text-red-500' : 'text-stone-400'}`}>
                            {input.length}/2000
                        </span>
                        <button 
                            onClick={handleSend}
                            disabled={input.length > 2000 || !input.trim() || isBotThinking}
                            className="bg-stone-900 dark:bg-white text-nobel-gold dark:text-[#0A0B0F] p-2 rounded-lg hover:opacity-90 transition-opacity h-9 w-9 flex items-center justify-center disabled:opacity-50"
                        >
                            <Send size={16} />
                        </button>
                    </div>
                 </div>
              </div>
            </Card>
       </div>
       <div className="hidden lg:block h-full">
            <Suggestions onSuggestionClick={(suggestion) => setInput(suggestion)} />
       </div>
    </div>
    <AnimatePresence>
        {showSettingsModal && (
            <ChatSettingsModal 
                onClose={() => setShowSettingsModal(false)}
                onSave={handleSaveSettings}
                initialModel={aiSettings.model}
                initialPersonality={aiSettings.personality}
                initialEnableHistory={aiSettings.enableHistory}
            />
        )}
         {showPromptLibrary && (
            <PromptLibraryModal
                onClose={() => setShowPromptLibrary(false)}
                onSelectPrompt={(promptText) => {
                    setInput(promptText);
                    setShowPromptLibrary(false);
                }}
            />
        )}
    </AnimatePresence>
    </>
  );
};
